import React from 'react'
import { Modal, ModalContent } from 'shared/components/common/modal'
import { t } from 'src/translations'
import { CLAIM_WINDOW_DAYS } from '../lib/claimWindow'
import { ExceptionKind } from '../lib/reasons'
import RecoveryModalHeader from './RecoveryModalHeader'
import RecoveryModalFooter from './RecoveryModalFooter'

// `viewRequest` routes to the guest's existing Intercom ticket without re-booting;
// `contactSupport` opens a new conversation carrying the exception's signed
// 'Selected refund method'. See ../README.md § Exception states.
export type ExceptionAction = 'viewRequest' | 'contactSupport'

interface Props {
  isOpen: boolean
  kind: ExceptionKind
  // Epoch millis. Absent or unusable → the undated message, per iOS's `.messageNoDate`.
  orderCreatedDateTime?: number | null
  onClose: () => void
  onAction: (action: ExceptionAction) => void
}

// Matches the sibling confirmation screens; the module's copy is en-US-only, so this is fixed.
const formatOrderDate = (ms: number): string =>
  new Date(ms).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })

/**
 * The two terminal recovery exceptions, sharing one surface: **already reported**
 * (`REFUND_ALREADY_ISSUED` — a request is in flight, so the primary action opens *that* ticket
 * rather than starting a second claim) and **outside claim window** (reached before D1, so nothing
 * is ever quoted). Terminal by design: no back chevron, and no 14-day fine print — here that
 * notice is the rule being enforced, not a caveat.
 */
export const RecoveryExceptionModal = ({
  isOpen,
  kind,
  orderCreatedDateTime,
  onClose,
  onAction
}: Props) => {
  const alreadyReported = kind === 'alreadyReported'

  const title = alreadyReported
    ? t('order-confirmation.recovery.exception.already-reported.title')
    : t('order-confirmation.recovery.exception.outside-window.title')

  const message = (): string => {
    if (alreadyReported) {
      return t('order-confirmation.recovery.exception.already-reported.message')
    }
    const dateKnown =
      orderCreatedDateTime != null && Number.isFinite(orderCreatedDateTime)
    return dateKnown
      ? t('order-confirmation.recovery.exception.outside-window.message', {
          days: CLAIM_WINDOW_DAYS,
          date: formatOrderDate(orderCreatedDateTime as number)
        })
      : t(
          'order-confirmation.recovery.exception.outside-window.message-no-date',
          { days: CLAIM_WINDOW_DAYS }
        )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      testId='recovery-exception-modal'
      className='refundSupportModal'
    >
      <ModalContent
        wrapperClassName='refundSupportWrapper'
        contentClassName='refundResolutionModal'
        ariaLabelledBy='recovery-exception-title'
        ariaDescribedBy='recovery-exception-message'
        // Both actions hand off to the Intercom panel, so the 500ms focus trap would fire after
        // unmount and steal focus back from the Messenger — same reason D3 opts out.
        disableFocusTrap
      >
        {/* No onBack: this is a dead end, not a step. */}
        <RecoveryModalHeader />
        <div className='refundResolutionBody'>
          <h2 id='recovery-exception-title' className='title' tabIndex={-1}>
            {title}
          </h2>
          <p id='recovery-exception-message' className='subtitle'>
            {message()}
          </p>
        </div>
        <RecoveryModalFooter>
          {alreadyReported && (
            <button
              type='button'
              className='resolutionCta'
              data-testid='recovery-exception-view-request'
              onClick={() => onAction('viewRequest')}
            >
              {t('order-confirmation.recovery.exception.already-reported.cta')}
            </button>
          )}
          {/* Primary for the window case, secondary next to the ticket link for the other. */}
          <button
            type='button'
            className={
              alreadyReported ? 'resolutionCtaSecondary' : 'resolutionCta'
            }
            data-testid='recovery-exception-contact-support'
            onClick={() => onAction('contactSupport')}
          >
            {t('order-confirmation.recovery.exception.contact-support')}
          </button>
        </RecoveryModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default RecoveryExceptionModal
